// src/prototype/pages/EADocumentUploadPage.tsx
// ============================================================
// DOCUMENT UPLOAD — design variant 1a, two paths splitting the fold
//
// Left:  upload what you already have. Any file, no preparation.
// Right: or hand us the exact shape, three instruments, lightest ask first.
//
// ─────────────────────────────────────────────────────────────────────────────
// WHY BOTH PATHS GET HALF THE FOLD.
//
// A page that leads with three workbooks reads as homework and gets closed. A page that
// only offers a drop zone never tells a client that a structured file is worth more than
// a PDF. Equal halves say both things at once: you can start now with what exists, and
// here is exactly what a better answer looks like.
//
// The right column is ordered by ASK, lightest first (D2, D1, D3), not by impact. D3
// moves the most and is last on purpose: a client who reads the heaviest ask first
// concludes the whole page is expensive.
// ─────────────────────────────────────────────────────────────────────────────
//
// NO PROGRESS FRACTION AND NO PERCENTAGE. A document count has no principled denominator,
// which is why the tracker was retired; this page does not reintroduce one under another
// name. What each uploaded file did is stated per file instead, which is a fact rather
// than a ratio.
//
// EVERY CONSEQUENCE LINE IS DERIVED. uploadOutcome() produces them from what the parser
// actually returned, so a file that could not be read says so rather than looking
// successful.
// ============================================================

import { useCallback, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Upload } from 'lucide-react';
import { EAInstrumentCard } from '../components/EAInstrumentCard';
import { EAHimssAssessmentsCard } from '../components/EAHimssAssessmentsCard';
import { INSTRUMENTS } from '../../data/coverage/gapInstruments';
import { useDemoUploads } from '../hooks/useDemoUploads';
import { evidenceForFile, uploadOutcome } from '../../lib/coverage/buildCoverageInputs';
import { markPersonalizationChanged } from '../hooks/useEvaluationRun';

/**
 * Lightest ask first. The order is the argument, not an accident: a client who reads the
 * heaviest ask first concludes the whole page is expensive. D4 is last because it is the
 * longest sit-down, even though it closes the largest gap.
 */
const CARD_ORDER = ['D2', 'D1', 'D3', 'D4'] as const;

const SUPPORTED = 'PDF, XLSX, CSV, TXT, MD, up to 25MB';

interface UploadedLine {
  id: string;
  name: string;
  note: string;
  applied: boolean;
}

export default function EADocumentUploadPage() {
  const navigate = useNavigate();
  const { documents, addDocument, removeDocument } = useDemoUploads();
  const [lines, setLines] = useState<UploadedLine[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const ingest = useCallback(
    async (files: FileList | File[]) => {
      for (const file of Array.from(files)) {
        // Read for evidence BEFORE recording. A file either applies fully or applies
        // nothing: evidenceForFile returns a complete source or null, and the store
        // writes one field, so there is no partial state to explain.
        const coverage = await evidenceForFile(file);
        const outcome = uploadOutcome(file.name, coverage);
        const rec = addDocument({
          name: file.name,
          size: file.size,
          ...(coverage ? { coverage } : {}),
        });
        setLines((prev) => [
          ...prev,
          { id: rec.id, name: file.name, note: outcome.note, applied: outcome.applied },
        ]);
      }
      markPersonalizationChanged();
    },
    [addDocument],
  );

  const cards = CARD_ORDER.map((code) =>
    INSTRUMENTS.find((i) => i.display?.code === code),
  ).filter((i): i is NonNullable<typeof i> => Boolean(i));

  return (
    <div className="min-h-screen bg-[#F5F7FA]">
      <div className="flex items-center justify-between px-6 h-9 bg-[#18365E]">
        <button
          type="button"
          onClick={() => navigate('/home')}
          className="inline-flex items-center gap-1.5 text-xs font-medium text-white/90 hover:text-white"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Back to Personalize
        </button>
        <span className="text-[10px] tracking-wider text-white/50">EA PERSONALIZATION</span>
      </div>

      <main className="px-7 pt-6 pb-8">
        <div className="flex items-baseline justify-between mb-4">
          <h1 className="text-xl font-bold text-[#18365E]">Documents</h1>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-[430px_1fr] gap-6 items-start">
          {/* ================= LEFT: what you already have ================= */}
          <section className="flex flex-col gap-3">
            <h2 className="text-[10px] font-bold tracking-widest text-[#717182]">
              UPLOAD WHAT YOU ALREADY HAVE
            </h2>

            <div
              onDragEnter={(e) => {
                e.preventDefault();
                setDragActive(true);
              }}
              onDragOver={(e) => e.preventDefault()}
              onDragLeave={() => setDragActive(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragActive(false);
                if (e.dataTransfer?.files?.length) void ingest(e.dataTransfer.files);
              }}
              className={`bg-white rounded-xl border-2 border-dashed px-7 py-9 text-center flex flex-col items-center gap-2.5 transition-colors ${
                dragActive ? 'border-[#FF6E4C] bg-[#FFF4F1]' : 'border-[#18365E]/35'
              }`}
            >
              <span className="w-11 h-11 rounded-full bg-[#EEF2F7] text-[#18365E] flex items-center justify-center">
                <Upload className="w-5 h-5" />
              </span>
              <p className="text-[15.5px] font-semibold text-[#18365E]">
                Drop any document you have today
              </p>
              <p className="text-xs leading-relaxed text-[#717182] max-w-[300px]">
                Strategic plan, board materials, annual report, governance policy. No
                preparation needed.
              </p>
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                className="mt-1 px-5 py-2 rounded-lg bg-[#18365E] hover:bg-[#22447a] text-white text-sm font-semibold transition-colors"
              >
                Browse files
              </button>
              <input
                ref={inputRef}
                type="file"
                multiple
                className="hidden"
                aria-label="Browse files"
                onChange={(e) => {
                  if (e.target.files?.length) void ingest(e.target.files);
                  e.target.value = '';
                }}
              />
              <p className="text-[10.5px] text-[#9aa1ad]">{SUPPORTED}</p>
            </div>

            {documents.length > 0 && (
              <div className="bg-white border border-[#e5e7eb] rounded-xl px-4 py-3.5">
                <h3 className="text-[10px] font-bold tracking-widest text-[#717182] mb-2.5">
                  UPLOADED
                </h3>
                <ul className="flex flex-col gap-2.5">
                  {documents.map((doc) => {
                    const line = lines.find((l) => l.id === doc.id);
                    return (
                      <li key={doc.id} className="flex gap-2.5 items-start">
                        {/* Green only when the file actually moved measures. A document
                            that was read for context is neutral, not a failure. */}
                        <span
                          className={`w-[7px] h-[7px] rounded-full mt-1.5 shrink-0 ${
                            doc.coverage ? 'bg-[#10B981]' : 'bg-[#c4cad4]'
                          }`}
                        />
                        <div className="min-w-0 flex-1">
                          <p className="text-xs font-semibold text-[#18365E] truncate">
                            {doc.name}
                          </p>
                          {line && (
                            <p className="text-[11px] leading-relaxed text-[#717182]">
                              {line.note}
                            </p>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => {
                            removeDocument(doc.id);
                            setLines((prev) => prev.filter((l) => l.id !== doc.id));
                            markPersonalizationChanged();
                          }}
                          aria-label={`Remove ${doc.name}`}
                          className="text-[10.5px] font-semibold text-[#717182] hover:text-[#18365E] shrink-0"
                        >
                          Remove
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </section>

          {/* ================= RIGHT: the exact shape ================= */}
          <section className="flex flex-col gap-3">
            <div className="flex items-baseline justify-between gap-3">
              <h2 className="text-[10px] font-bold tracking-widest text-[#717182]">
                OR HAND US THE EXACT SHAPE, FOUR INSTRUMENTS
              </h2>
              <span className="text-[10.5px] text-[#9aa1ad] whitespace-nowrap">
                Lightest ask first
              </span>
            </div>

            {cards.map((instrument) => (
              <EAInstrumentCard key={instrument.id} instrument={instrument} />
            ))}

            <p className="text-[11px] leading-relaxed text-[#717182]">
              Other documents still help. They inform context and narrative. The four named
              here are the ones that move scores directly.
            </p>

            {/* E1 (Oct 21): optional HIMSS assessments. Context, not a scoring input. */}
            <h2 className="text-[10px] font-bold tracking-widest text-[#717182] mt-2">
              ESTABLISHED MATURITY ASSESSMENTS, IF YOU HOLD THEM
            </h2>
            <EAHimssAssessmentsCard onUploadFile={(f) => void ingest([f])} />
          </section>
        </div>
      </main>
    </div>
  );
}
